import { defineStore } from "pinia";

import { useToast } from "@/composables/useToast.ts";
import { IPrintersStore } from "@/types/printers.ts";
import axios from "@/utils/axios.ts";

const { error } = useToast();

export const usePrintersStore = defineStore("printers", {
  state: (): IPrintersStore => ({
    printers: [],
    unusedPrinters: [],
    isPrintersLoader: true,
    isOpenPrinterSideBar: false,
    labelsList: [],
    labelsListLoader: false,
  }),
  getters: {},
  actions: {
    openPrinterSideBar(value: boolean) {
      this.isOpenPrinterSideBar = value;
    },

    setPrintersList(printers: any) {
      this.printers = printers;
    },

    async fetchAll(params = {}) {
      return axios.get("/printers", {
        params,
      });
    },
    async fetchSingle(id: any) {
      return axios.get(`/printers/${id}`);
    },
    async update(
      printer = {
        id: 0,
      },
    ) {
      return axios.put(`/printers/${printer.id}`, printer);
    },

    updatePrinter(printer: any, callback: () => void) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/printers/${printer.id}`, printer)
          .then((response) => {
            callback && callback();
            resolve(response);
          })
          .catch((error) => reject(error));
      });
    },

    async create(printer = {}) {
      return axios.post("/printers", printer);
    },

    async remove(id: any) {
      return axios.delete(`/printers/${id}`);
    },

    async removePrinter(payload: any, callback?: () => void) {
      const { printer, label } = payload;

      if (!printer || !label) return;

      try {
        await axios.delete(`/printers/${printer.id}`, {
          params: {
            labelId: label.id,
          },
        });
        callback && callback();
      }
      catch (errorResponse: any) {
        error(errorResponse.response?.data?.message);
      }
    },

    async setToUser(
      userId: string,
      printerId: string,
      labelId: string,
      callback?: () => void,
    ) {
      try {
        await axios.put(`/users/${userId}/printers`, { printerId, labelId });
        callback && callback();
      }
      catch (errorResponse: any) {
        error(errorResponse.response?.data?.message);
      }
    },

    async getUser(user: any) {
      this.isPrintersLoader = true;
      try {
        const { data } = await axios.get(`/users/${user}/printers`);
        this.printers = data;
      }
      catch (error) {
        console.log(error);
      }
      finally {
        this.isPrintersLoader = false;
      }
    },

    async detachingPrinter(
      userId: number,
      printerId: number,
      callback?: () => void,
    ) {
      this.isPrintersLoader = true;
      try {
        await axios.delete(`/users/${userId}/printers/${printerId}`);
        await this.getUser(userId);
        await this.getUnusedPrinters(userId);
        callback && callback();
      }
      catch (errorResponse: any) {
        error(errorResponse.response?.data?.message);
      }
      finally {
        this.isPrintersLoader = false;
      }
    },

    async clearToUser({ printer, user }: { printer: any, user: any }) {
      return axios.post(`/users/${user}/printers`, { printer_id: printer });
    },
    async getUnusedPrinters(user: any) {
      try {
        const { data } = await axios.get(`/users/${user}/unused-printers`);
        this.unusedPrinters = data;
      }
      catch (error) {
        console.log(error);
      }
    },
    async fetchLabels(payload = {}) {
      try {
        this.labelsListLoader = true;
        const { data } = await axios.get("/labels", payload);
        this.labelsList = data;
      }
      catch (error) {
        console.log(error);
      }
      finally {
        this.labelsListLoader = false;
      }
    },

    async updateLabel(id: number, title: string, callback?: () => void) {
      try {
        await axios.patch(`/labels/${id}`, {
          title,
        });
        callback && callback();
      }
      catch (e) {
        error(e as any);
      }
    },
  },
});
